/**
 * Thin WebSocket client for the Doneverse API realtime endpoint.
 *
 * Shared between the dashboard (browser WebSocket) and the mobile app (React
 * Native WebSocket). The global `WebSocket` API exposed by both is enough —
 * we deliberately avoid Socket.IO and any DOM-only types.
 *
 * Protocol (see docs/api-websockets-supabase-realtime.md and
 * apps/api/src/realtime/server.ts):
 *   1. open wss://<api>/v1/realtime
 *   2. send {type:'auth', token:'<Auth0 access token>'}
 *   3. receive {type:'connected', protocol_version, user_id}
 *   4. receive {type:'notification.created', ...}
 *
 * Reconnect with exponential backoff (capped). Re-auth on every reconnect —
 * the token may have refreshed in the meantime.
 */

import type { CallRealtimeEvent, ContactMessage, NotificationType } from '@doneverse/shared'

const RECONNECT_INITIAL_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_JITTER_MS = 500
/**
 * How often the client re-asserts its focused chat while one is open. Must be
 * comfortably under the server's focus TTL (120s) so the key never lapses
 * mid-session. Re-asserting also re-sends focus after a reconnect.
 */
const FOCUS_REASSERT_MS = 60_000

export interface ApiRealtimeNotificationEvent {
  type: 'notification.created'
  id: string
  occurred_at: string
  data: {
    notification_id: string
    type: NotificationType
    title: string
    body: string | null
    reference_type: string | null
    reference_id: string | null
  }
}

export interface ApiRealtimeMessageEvent {
  type: 'message.created'
  id: string
  occurred_at: string
  /** Conversation key from this client's POV (matches `openChatContactId`). */
  contact_id: string
  data: ContactMessage
}

/**
 * Huddle lifecycle in a conversation this user belongs to. The wire shape is
 * defined once in @doneverse/shared and shared with the API server.
 */
export type ApiRealtimeCallEvent = CallRealtimeEvent

interface ConnectedEvent {
  type: 'connected'
  protocol_version: number
  user_id: string
}

interface ServerErrorEvent {
  type: 'error'
  code: string
  message: string
}

type ServerEvent =
  | ConnectedEvent
  | ServerErrorEvent
  | ApiRealtimeNotificationEvent
  | ApiRealtimeMessageEvent
  | ApiRealtimeCallEvent
  | { type: 'pong' }

export interface ApiRealtimeClientOptions {
  url: string
  getToken: () => Promise<string>
  onNotification: (event: ApiRealtimeNotificationEvent) => void
  /** Live chat message for an open conversation — append it directly. */
  onMessage?: (event: ApiRealtimeMessageEvent) => void
  /** Huddle started / ended / roster changed in one of this user's conversations. */
  onCallEvent?: (event: ApiRealtimeCallEvent) => void
  /** Fired after a (re)connect, so the caller can re-hydrate state a dropped
   * socket may have missed (a huddle that started while we were offline). */
  onConnected?: () => void
  /** Optional log surface (defaults to console). */
  log?: Pick<Console, 'warn' | 'error'>
}

export interface ApiRealtimeClient {
  /** Tear down the connection and stop reconnecting. */
  close: () => void
  /**
   * Report which chat the user is actively viewing (its `contact_id`), or
   * `null` on blur. The server skips notifications for a focused chat. The
   * value is re-asserted automatically on every reconnect and on an interval.
   */
  setFocus: (contactId: string | null) => void
}

export function connectApiRealtime(opts: ApiRealtimeClientOptions): ApiRealtimeClient {
  const log = opts.log ?? console
  let closedByCaller = false
  let socket: WebSocket | null = null
  let reconnectAttempt = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let focusContactId: string | null = null

  const sendFocus = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'set_focus', contact_id: focusContactId }))
    }
  }

  // Re-assert a non-null focus periodically so the server's TTL never lapses
  // while a chat stays open.
  const focusTimer = setInterval(() => {
    if (focusContactId !== null) sendFocus()
  }, FOCUS_REASSERT_MS)

  const scheduleReconnect = () => {
    if (closedByCaller) return
    const backoff = Math.min(RECONNECT_INITIAL_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS)
    const jitter = Math.floor(Math.random() * RECONNECT_JITTER_MS)
    reconnectAttempt++
    reconnectTimer = setTimeout(() => {
      void open()
    }, backoff + jitter)
  }

  const open = async () => {
    if (closedByCaller) return

    let token: string
    try {
      token = await opts.getToken()
    } catch (err) {
      log.warn('[api-realtime] failed to fetch token, will retry', err)
      scheduleReconnect()
      return
    }
    if (closedByCaller) return

    const ws = new WebSocket(opts.url)
    socket = ws

    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'auth', token }))
    })

    ws.addEventListener('message', (event) => {
      let parsed: ServerEvent
      try {
        parsed = JSON.parse(event.data as string) as ServerEvent
      } catch {
        return
      }
      switch (parsed.type) {
        case 'connected':
          // Auth succeeded — reset backoff so the next disconnect retries
          // promptly rather than at the previous slow interval.
          reconnectAttempt = 0
          // Re-assert focus: the server's per-socket state reset on reconnect.
          if (focusContactId !== null) sendFocus()
          // Events are at-most-once with no replay, so anything that happened
          // while we were disconnected has to be re-fetched over REST.
          opts.onConnected?.()
          return
        case 'notification.created':
          opts.onNotification(parsed)
          return
        case 'message.created':
          opts.onMessage?.(parsed)
          return
        case 'call.started':
        case 'call.ended':
        case 'call.participants_changed':
        case 'call.ring_changed':
        case 'call.ring_resolved':
          opts.onCallEvent?.(parsed)
          return
        case 'error':
          log.warn('[api-realtime] server error', parsed.code, parsed.message)
          return
        default:
          return
      }
    })

    ws.addEventListener('close', () => {
      socket = null
      scheduleReconnect()
    })

    ws.addEventListener('error', () => {
      // `close` will fire too; reconnect logic lives there.
    })
  }

  void open()

  return {
    close: () => {
      closedByCaller = true
      clearInterval(focusTimer)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (socket && socket.readyState <= WebSocket.OPEN) {
        socket.close(1000, 'client_closed')
      }
      socket = null
    },
    setFocus: (contactId: string | null) => {
      focusContactId = contactId
      sendFocus()
    },
  }
}

/**
 * Convert a REST API base URL into the realtime WebSocket URL.
 *   http://localhost:3001        → ws://localhost:3001/v1/realtime
 *   https://api.doneverse.com    → wss://api.doneverse.com/v1/realtime
 */
export function apiRealtimeUrl(apiBaseUrl: string): string {
  const trimmed = apiBaseUrl.replace(/\/$/, '')
  if (trimmed.startsWith('https://')) return `wss://${trimmed.slice('https://'.length)}/v1/realtime`
  if (trimmed.startsWith('http://')) return `ws://${trimmed.slice('http://'.length)}/v1/realtime`
  return `${trimmed}/v1/realtime`
}
